import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Save,
    X,
    AlertCircle,
    FileWarning,
} from "lucide-react";
import axiosInstance from "../../../src/api/axiosInstance";
import Authenticated from "../../layout/Authenticated";

const NewMedicine = () => {
    const navigate = useNavigate();

    // HSN Code to GST Rate Mapping for Pharmaceutical Products
    const hsnToGstMapping = {
        // Medicines and pharmaceutical products
        '30049099': 12, // Other medicaments (general medicines)
        '30041000': 12, // Penicillins and their derivatives
        '30042000': 12, // Antibiotics (other than penicillins)
        '30043100': 12, // Insulin
        '30043200': 12, // Other hormones
        '30043900': 12, // Other hormones and derivatives
        '30044000': 12, // Glycosides and their salts
        '30045000': 12, // Vitamins and their derivatives
        '30046000': 12, // Provitamins and vitamins
        '30049011': 12, // Ayurvedic medicines
        '30049012': 12, // Unani medicines
        '30049013': 12, // Siddha medicines
        '30049014': 12, // Homeopathic medicines
        '30049015': 12, // Biochemic medicines
        '30049091': 12, // Traditional systems of medicine
        '30049092': 12, // Other medicaments containing vitamins
        '30051000': 12, // Adhesive dressings
        '30059090': 12, // Other bandages and similar articles
        '30063000': 5,  // Opacifying preparations for X-ray examinations
        '33061000': 18, // Dentifrices
        '33062000': 18, // Yarn used to clean between the teeth (dental floss)
        '33071000': 18, // Pre-shave, shaving or after-shave preparations
        '33072000': 18, // Personal deodorants and antiperspirants
        '33073000': 18, // Perfumed bath salts and other bath preparations
        '33074100': 18, // "Agarbatti" and other odoriferous preparations
        '33074900': 18, // Other preparations for perfuming or deodorizing rooms
        '33079000': 18  // Other cosmetic and toilet preparations
    };

    // Function to auto-fill GST based on HSN code
    const handleHsnCodeChange = (e) => {
        const { value } = e.target;
        setFormData(prev => ({ ...prev, hsn_code: value }));

        // Auto-fill GST rate based on HSN code
        if (hsnToGstMapping[value]) {
            setFormData(prev => ({
                ...prev,
                hsn_code: value,
                gst_percentage: hsnToGstMapping[value].toString()
            }));

            // Show notification about auto-filled GST
            showNotification(`GST rate auto-filled: ${hsnToGstMapping[value]}% for HSN ${value}`, "success");
        } else {
            setFormData(prev => ({ ...prev, hsn_code: value }));
        }

        // Clear any existing error
        setErrors(prev => ({ ...prev, hsn_code: "" }));
    };

    const [formData, setFormData] = useState({
        name: "",
        generic_name: "",
        category: "",
        strength: "",
        uom: "",
        units_per_package: "",
        manufacturer: "",
        reorder_level: "",
        max_stock_level: "",
        description: "",
        storage_conditions: "",
        drug_code: "",
        hsn_code: "",
        schedule: "",
        therapeutic_class: "",
        composition: "",
        gst_percentage: "12",
        rack_number: "",
        is_refrigerated: false,
        is_prescription_required: true,
        bar_code: "",
        minimum_expiry_alert_days: "90",
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [alertType, setAlertType] = useState("success");

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({ ...prevData, [name]: value }));
        setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name) newErrors.name = "Name is required.";
        if (!formData.category) newErrors.category = "Category is required.";
        if (!formData.strength) newErrors.strength = "Strength is required.";
        if (!formData.uom) newErrors.uom = "UOM is required.";
        if (!formData.units_per_package)
            newErrors.units_per_package = "Units per package is required.";
        if (!formData.manufacturer)
            newErrors.manufacturer = "Manufacturer is required.";
        if (!formData.reorder_level)
            newErrors.reorder_level = "Reorder level is required.";
        // if (!formData.hsn_code)
        //     newErrors.hsn_code = "HSN code is required for GST compliance.";
        if (formData.units_per_package && formData.units_per_package < 1)
            newErrors.units_per_package = "Units per package must be at least 1.";
        if (formData.reorder_level < 0)
            newErrors.reorder_level = "Reorder level cannot be negative.";

        return newErrors;
    };

    const showNotification = (message, type = "success") => {
        setAlertMessage(message);
        setAlertType(type);
        setShowAlert(true);
        setTimeout(() => setShowAlert(false), 5000);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            showNotification("Please fill in all required fields.", "error");
            return;
        }

        setIsSubmitting(true);
        try {
            await axiosInstance.post("/medicine", {
                name: formData.name,
                generic_name: formData.generic_name,
                category: formData.category,
                strength: formData.strength,
                uom: formData.uom,
                units_per_package: Number(formData.units_per_package),
                manufacturer: formData.manufacturer,
                reorder_level: Number(formData.reorder_level),
                max_stock_level: formData.max_stock_level ? Number(formData.max_stock_level) : null,
                description: formData.description,
                storage_conditions: formData.storage_conditions,
                drug_code: formData.drug_code,
                hsn_code: formData.hsn_code,
                schedule: formData.schedule,
                therapeutic_class: formData.therapeutic_class,
                composition: formData.composition,
                gst_percentage: Number(formData.gst_percentage),
                rack_number: formData.rack_number,
                is_refrigerated: formData.is_refrigerated,
                is_prescription_required: formData.is_prescription_required,
                bar_code: formData.bar_code,
                minimum_expiry_alert_days: Number(formData.minimum_expiry_alert_days),
            });

            navigate("/medicine", {
                state: { successMessage: "Medicine added successfully!" },
            });
        } catch (error) {
            console.error("Error adding medicine:", error);
            const serverMsg = error?.response?.data?.error;
            // If backend reports duplicate medicine, map to form fields so user sees validation inline
            if (serverMsg && serverMsg.toLowerCase().includes('already exists')) {
                setErrors(prev => ({
                    ...prev,
                    name: 'Medicine with this name and strength already exists',
                    strength: 'Medicine with this name and strength already exists',
                }));
                showNotification(serverMsg, "error");
            } else {
                showNotification(serverMsg || "Failed to add medicine.", "error");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Authenticated>
            <div className="p-6 max-w-7xl mx-auto">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">Add New Medicine</h1>
                        <p className="text-gray-500 text-sm">Register a new medicine in your inventory</p>
                    </div>
                </div>

                {showAlert && (
                    <div
                        className={`mb-6 p-4 rounded-lg flex items-start justify-between ${alertType === "success"
                            ? "bg-green-50 text-green-800 border-l-4 border-green-500"
                            : "bg-red-50 text-red-800 border-l-4 border-red-500"
                            }`}
                    >
                        <div className="flex items-center">
                            {alertType === "success" ? (
                                <Save size={20} className="mr-3 text-green-500" />
                            ) : (
                                <FileWarning size={20} className="mr-3 text-red-500" />
                            )}
                            <span>{alertMessage}</span>
                        </div>
                        <button onClick={() => setShowAlert(false)} className="text-gray-500 hover:text-gray-700">
                            <X size={18} />
                        </button>
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                            {/* Name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder="Enter name"
                                    className={`w-full px-3 py-2.5 bg-white border ${errors.name ? "border-red-500" : "border-gray-200"
                                        } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                                />
                                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                            </div>

                            {/* Generic Name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Generic Name (e.g., “Acetaminophen” for Paracetamol)
                                </label>
                                <input
                                    type="text"
                                    name="generic_name"
                                    value={formData.generic_name}
                                    onChange={handleChange}
                                    placeholder="Enter generic name"
                                    className={`w-full px-3 py-2.5 bg-white border ${errors.generic_name ? "border-red-500" : "border-gray-200"
                                        } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                                />
                                {errors.generic_name && <p className="text-red-500 text-xs mt-1">{errors.generic_name}</p>}
                            </div>

                            {/* Category */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Category <span className="text-red-500">*</span>
                                </label>
                                <select
                                    name="category"
                                    value={formData.category}
                                    onChange={handleChange}
                                    className={`w-full px-3 py-2.5 bg-white border ${errors.category ? "border-red-500" : "border-gray-200"
                                        } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                                >
                                    <option value="">Select Category</option>
                                    <option value="TABLET">Tablet</option>
                                    <option value="CAPSULES">Capsules</option>
                                    <option value="SYRUP">Syrup</option>
                                    <option value="INJECTION">Injection</option>
                                    <option value="AMPOULES">Ampoules</option>
                                    <option value="CREAM">Cream</option>
                                    <option value="DROPS">Drops</option>
                                    <option value="INHELER">Inheler</option>
                                    <option value="POWDER">Powder</option>
                                    <option value="CAPTAB">Captab</option>
                                    <option value="GEL">Gel</option>
                                    <option value="LIQUID">Liquid</option>
                                    <option value="ItemType">Item Type</option>
                                    <option value="NA">NA</option>
                                    <option value="VIAL">Vial</option>

                                </select>
                                {errors.category && <p className="text-red-500 text-xs mt-1">{errors.category}</p>}
                            </div>

                            {/* Strength */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Strength <span className="text-red-500">*</span> (e.g., “500mg” for Paracetamol)
                                </label>
                                <input
                                    type="text"
                                    name="strength"
                                    value={formData.strength}
                                    onChange={handleChange}
                                    placeholder="Enter strength (e.g., 500mg)"
                                    className={`w-full px-3 py-2.5 bg-white border ${errors.strength ? "border-red-500" : "border-gray-200"
                                        } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                                />
                                {errors.strength && <p className="text-red-500 text-xs mt-1">{errors.strength}</p>}
                            </div>

                            {/* UOM */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    UOM <span className="text-red-500">*</span>
                                </label>
                                <select
                                    name="uom"
                                    value={formData.uom}
                                    onChange={handleChange}
                                    className={`w-full px-3 py-2.5 bg-white border ${errors.uom ? "border-red-500" : "border-gray-200"
                                        } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                                >
                                    <option value="">Select UOM</option>
                                    <option value="Tablet">Tablet</option>
                                    <option value="Capsule">Capsule</option>
                                    <option value="ml">ml</option>
                                    <option value="mg">mg</option>
                                    <option value="g">g</option>
                                    <option value="kg">kg</option>
                                    <option value="Unit">Unit</option>
                                    <option value="Strip">Strip</option>
                                    <option value="Vial">Vial</option>
                                    <option value="Ampoule">Ampoule</option>
                                    <option value="Bottle">Bottle</option>
                                </select>
                                {errors.uom && <p className="text-red-500 text-xs mt-1">{errors.uom}</p>}
                            </div>

                            {/* Units per Package */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Units per Package <span className="text-red-500">*</span> (e.g., 10 tablets/strip)
                                </label>
                                <input
                                    type="number"
                                    name="units_per_package"
                                    value={formData.units_per_package}
                                    onChange={handleChange}
                                    placeholder="Enter units per package"
                                    className={`w-full px-3 py-2.5 bg-white border ${errors.units_per_package ? "border-red-500" : "border-gray-200"
                                        } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                                />
                                {errors.units_per_package && <p className="text-red-500 text-xs mt-1">{errors.units_per_package}</p>}
                            </div>

                            {/* Manufacturer */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Manufacturer <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="manufacturer"
                                    value={formData.manufacturer}
                                    onChange={handleChange}
                                    placeholder="Enter Manufacturer"
                                    className={`w-full px-3 py-2.5 bg-white border ${errors.manufacturer ? "border-red-500" : "border-gray-200"
                                        } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                                />
                                {errors.manufacturer && <p className="text-red-500 text-xs mt-1">{errors.manufacturer}</p>}
                            </div>

                            {/* Reorder Level */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Reorder Level <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="number"
                                    name="reorder_level"
                                    value={formData.reorder_level}
                                    onChange={handleChange}
                                    placeholder="Enter Reorder Level"
                                    className={`w-full px-3 py-2.5 bg-white border ${errors.reorder_level ? "border-red-500" : "border-gray-200"
                                        } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                                />
                                {errors.reorder_level && <p className="text-red-500 text-xs mt-1">{errors.reorder_level}</p>}
                            </div>

                            {/* Max Stock Level */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Max Stock Level
                                </label>
                                <input
                                    type="number"
                                    name="max_stock_level"
                                    value={formData.max_stock_level}
                                    onChange={handleChange}
                                    placeholder="Enter Max Stock Level"
                                    className={`w-full px-3 py-2.5 bg-white border ${errors.max_stock_level ? "border-red-500" : "border-gray-200"
                                        } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                                />
                                {errors.max_stock_level && <p className="text-red-500 text-xs mt-1">{errors.max_stock_level}</p>}
                            </div>

                            {/* Description */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Description
                                </label>
                                <input
                                    type="text"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    placeholder="Enter description"
                                    className={`w-full px-3 py-2.5 bg-white border ${errors.description ? "border-red-500" : "border-gray-200"
                                        } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                                />
                                {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
                            </div>

                            {/* Storage Conditions */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Storage Conditions (e.g., “Store below 25°C”)
                                </label>
                                <input
                                    type="text"
                                    name="storage_conditions"
                                    value={formData.storage_conditions}
                                    onChange={handleChange}
                                    placeholder="Enter storage conditions"
                                    className={`w-full px-3 py-2.5 bg-white border ${errors.storage_conditions ? "border-red-500" : "border-gray-200"
                                        } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                                />
                                {errors.storage_conditions && <p className="text-red-500 text-xs mt-1">{errors.storage_conditions}</p>}
                            </div>

                            {/* Drug Code */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Drug Code (DCGI)
                                </label>
                                <input
                                    type="text"
                                    name="drug_code"
                                    value={formData.drug_code}
                                    onChange={handleChange}
                                    placeholder="Enter drug code"
                                    className={`w-full px-3 py-2.5 bg-white border ${errors.drug_code ? "border-red-500" : "border-gray-200"
                                        } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                                />
                                {errors.drug_code && <p className="text-red-500 text-xs mt-1">{errors.drug_code}</p>}
                            </div>

                            {/* HSN Code */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    HSN Code (for GST) {/* <span className="text-red-500">*</span> */}
                                </label>
                                <input
                                    type="text"
                                    name="hsn_code"
                                    value={formData.hsn_code}
                                    onChange={handleHsnCodeChange}
                                    placeholder="Enter HSN code (e.g., 30049099)"
                                    list="hsn-codes"
                                    className={`w-full px-3 py-2.5 bg-white border ${errors.hsn_code ? "border-red-500" : "border-gray-200"
                                        } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                                />
                                <datalist id="hsn-codes">
                                    <option value="30049099">30049099 - General Medicines (12%)</option>
                                    <option value="30041000">30041000 - Penicillins (12%)</option>
                                    <option value="30042000">30042000 - Antibiotics (12%)</option>
                                    <option value="30043100">30043100 - Insulin (12%)</option>
                                    <option value="30043200">30043200 - Hormones (12%)</option>
                                    <option value="30044000">30044000 - Glycosides (12%)</option>
                                    <option value="30045000">30045000 - Vitamins (12%)</option>
                                    <option value="30049011">30049011 - Ayurvedic Medicines (12%)</option>
                                    <option value="30049012">30049012 - Unani Medicines (12%)</option>
                                    <option value="30049013">30049013 - Siddha Medicines (12%)</option>
                                    <option value="30049014">30049014 - Homeopathic Medicines (12%)</option>
                                    <option value="30051000">30051000 - Adhesive Dressings (12%)</option>
                                    <option value="30063000">30063000 - X-ray Preparations (5%)</option>
                                </datalist>
                                {errors.hsn_code && <p className="text-red-500 text-xs mt-1">{errors.hsn_code}</p>}
                                {formData.hsn_code && formData.gst_percentage && (
                                    <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded text-xs">
                                        <div className="text-blue-800 font-medium mb-2">GST Information (4 Types in India):</div>
                                        <div className="text-blue-600 space-y-1">
                                            <div><strong>Total GST:</strong> {formData.gst_percentage}%</div>
                                            {parseFloat(formData.gst_percentage) > 0 && (
                                                <>
                                                    <div className="font-medium text-blue-700 mt-2">1. Intra-State Transactions (State to State):</div>
                                                    <div className="ml-2">
                                                        • CGST: {(parseFloat(formData.gst_percentage) / 2).toFixed(1)}%<br />
                                                        • SGST: {(parseFloat(formData.gst_percentage) / 2).toFixed(1)}%
                                                    </div>

                                                    <div className="font-medium text-blue-700 mt-2">2. Intra-UT Transactions (Within Union Territory):</div>
                                                    <div className="ml-2">
                                                        • CGST: {(parseFloat(formData.gst_percentage) / 2).toFixed(1)}%<br />
                                                        • UTGST: {(parseFloat(formData.gst_percentage) / 2).toFixed(1)}%
                                                    </div>

                                                    <div className="font-medium text-blue-700 mt-2">3. Inter-State/UT Transactions:</div>
                                                    <div className="ml-2">
                                                        • IGST: {formData.gst_percentage}%
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Schedule */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Drug Schedule
                                </label>
                                <select
                                    name="schedule"
                                    value={formData.schedule}
                                    onChange={handleChange}
                                    className={`w-full px-3 py-2.5 bg-white border ${errors.schedule ? "border-red-500" : "border-gray-200"
                                        } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                                >
                                    <option value="">Select Schedule</option>
                                    <option value="H">Schedule H</option>
                                    <option value="H1">Schedule H1</option>
                                    <option value="X">Schedule X</option>
                                    <option value="G">Schedule G</option>
                                    <option value="OTC">OTC (Over The Counter)</option>
                                    <option value="Narcotic">Narcotic</option>
                                    <option value="Psychotropic">Psychotropic</option>
                                </select>
                                {errors.schedule && <p className="text-red-500 text-xs mt-1">{errors.schedule}</p>}
                            </div>

                            {/* Therapeutic Class */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Therapeutic Class
                                </label>
                                <input
                                    type="text"
                                    name="therapeutic_class"
                                    value={formData.therapeutic_class}
                                    onChange={handleChange}
                                    placeholder="e.g., Antibiotic, Analgesic, Antihistamine"
                                    className={`w-full px-3 py-2.5 bg-white border ${errors.therapeutic_class ? "border-red-500" : "border-gray-200"
                                        } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                                />
                                {errors.therapeutic_class && <p className="text-red-500 text-xs mt-1">{errors.therapeutic_class}</p>}
                            </div>

                            {/* GST Percentage */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    GST Percentage (%)
                                </label>
                                <select
                                    name="gst_percentage"
                                    value={formData.gst_percentage}
                                    onChange={handleChange}
                                    className={`w-full px-3 py-2.5 bg-white border ${errors.gst_percentage ? "border-red-500" : "border-gray-200"
                                        } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                                >
                                    <option value="0">0%</option>
                                    <option value="5">5%</option>
                                    <option value="12">12%</option>
                                    <option value="18">18%</option>
                                    <option value="28">28%</option>
                                </select>
                                {errors.gst_percentage && <p className="text-red-500 text-xs mt-1">{errors.gst_percentage}</p>}
                            </div>

                            {/* Rack Number */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Rack Number
                                </label>
                                <input
                                    type="text"
                                    name="rack_number"
                                    value={formData.rack_number}
                                    onChange={handleChange}
                                    placeholder="e.g., A1, B2, C3"
                                    className={`w-full px-3 py-2.5 bg-white border ${errors.rack_number ? "border-red-500" : "border-gray-200"
                                        } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                                />
                                {errors.rack_number && <p className="text-red-500 text-xs mt-1">{errors.rack_number}</p>}
                            </div>

                            {/* Bar Code */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Bar Code
                                </label>
                                <input
                                    type="text"
                                    name="bar_code"
                                    value={formData.bar_code}
                                    onChange={handleChange}
                                    placeholder="Enter barcode"
                                    className={`w-full px-3 py-2.5 bg-white border ${errors.bar_code ? "border-red-500" : "border-gray-200"
                                        } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                                />
                                {errors.bar_code && <p className="text-red-500 text-xs mt-1">{errors.bar_code}</p>}
                            </div>

                            {/* Minimum Expiry Alert Days */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Expiry Alert Days
                                </label>
                                <input
                                    type="number"
                                    name="minimum_expiry_alert_days"
                                    value={formData.minimum_expiry_alert_days}
                                    onChange={handleChange}
                                    placeholder="Days before expiry to alert"
                                    className={`w-full px-3 py-2.5 bg-white border ${errors.minimum_expiry_alert_days ? "border-red-500" : "border-gray-200"
                                        } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                                />
                                {errors.minimum_expiry_alert_days && <p className="text-red-500 text-xs mt-1">{errors.minimum_expiry_alert_days}</p>}
                            </div>

                        </div>

                        {/* Composition - Full width */}
                        <div className="mt-6">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Composition
                            </label>
                            <textarea
                                name="composition"
                                value={formData.composition}
                                onChange={handleChange}
                                placeholder="Enter complete composition of the medicine"
                                rows="3"
                                className={`w-full px-3 py-2.5 bg-white border ${errors.composition ? "border-red-500" : "border-gray-200"
                                    } rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
                            />
                            {errors.composition && <p className="text-red-500 text-xs mt-1">{errors.composition}</p>}
                        </div>

                        {/* Checkbox fields */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    name="is_refrigerated"
                                    checked={formData.is_refrigerated}
                                    onChange={(e) => setFormData(prev => ({ ...prev, is_refrigerated: e.target.checked }))}
                                    className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                                />
                                <label className="ml-2 text-sm font-medium text-gray-700">
                                    Requires Refrigeration
                                </label>
                            </div>
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    name="is_prescription_required"
                                    checked={formData.is_prescription_required}
                                    onChange={(e) => setFormData(prev => ({ ...prev, is_prescription_required: e.target.checked }))}
                                    className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                                />
                                <label className="ml-2 text-sm font-medium text-gray-700">
                                    Prescription Required
                                </label>
                            </div>
                        </div>

                        <div className="flex items-center text-gray-500 text-sm mt-5">
                            <AlertCircle size={16} className="mr-2" />
                            <span>All fields marked with <span className="text-red-500">*</span> are required</span>
                        </div>

                        {/* GST Information Panel */}
                        <div className="mt-6 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg">
                            <div className="flex items-start">
                                <div className="flex-shrink-0">
                                    <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                                        <span className="text-indigo-600 font-bold text-sm">₹</span>
                                    </div>
                                </div>
                                <div className="ml-3">
                                    <h3 className="text-sm font-medium text-indigo-800">4 Types of GST in India for Medicines</h3>
                                    <div className="mt-2 text-xs text-indigo-600 space-y-1">
                                        <div><strong>1. CGST (Central GST):</strong> Central government tax component</div>
                                        <div><strong>2. SGST (State GST):</strong> State government tax (for intra-state transactions)</div>
                                        <div><strong>3. IGST (Integrated GST):</strong> Combined tax (for inter-state transactions)</div>
                                        <div><strong>4. UTGST (Union Territory GST):</strong> UT government tax (instead of SGST in UTs)</div>
                                        <div className="mt-2 text-indigo-500">
                                            <em><strong>Application:</strong></em><br />
                                            • <strong>Same State:</strong> CGST + SGST<br />
                                            • <strong>Same UT:</strong> CGST + UTGST<br />
                                            • <strong>Different States/UTs:</strong> IGST only
                                        </div>
                                        <div className="mt-2 text-indigo-500">
                                            <em><strong>Union Territories:</strong> Delhi, Chandigarh, Puducherry, Ladakh, J&K, Andaman & Nicobar, Lakshadweep, Dadra & Nagar Haveli, Daman & Diu</em>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row justify-start items-stretch sm:items-center mt-6 space-y-3 sm:space-y-0 sm:space-x-3">
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className={`w-full sm:w-auto px-6 py-2.5 rounded-lg flex items-center justify-center ${isSubmitting
                                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                    : "bg-teal-600 text-white hover:bg-teal-700 shadow-md hover:shadow-lg transition-all duration-200"
                                    }`}
                            >
                                <Save size={18} className="mr-2" />
                                {isSubmitting ? "Submitting..." : "Submit"}
                            </button>
                            <button
                                type="button"
                                onClick={() => navigate("/medicine")}
                                className="w-full sm:w-auto px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </Authenticated>
    );
};

export default NewMedicine;